import {gql} from 'graphql-request';
import {useGqlRequest} from '@/api/request';
import {
  AnalysisItem, BasicAnalysisResult, OfficeWorkerPointByResult, PresumePopulationPointByResult
} from '@/map/@types';
import {AreaAnalyticsVars} from '@/map/@types/query-variables';
import {AreaOverlayType} from '@/map/enums';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {getAreaQueryVariables} from './variables';

export const useQueryBasicAnalysis = (item: AnalysisItem) => {
  const query =
    item.type === AreaOverlayType.CIRCLE
      ? AREA_ANALYZER_POINT
      : item.type === AreaOverlayType.POLYGON
      ? AREA_ANALYZER_POLYGON
      : AREA_ANALYZER_HJD;
  const request = useGqlRequest({query});

  const queryFn = ():
    | undefined
    | Promise<{
        result?: BasicAnalysisResult;
      }> => {
    const variant: (AreaAnalyticsVars & {m2?: number}) | undefined =
      getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    if (item.polygon?.m2) {
      variant.m2 = item.polygon?.m2;
    }

    return request(variant);
  };

  const id = item?.id;

  const select = (data?: {result?: BasicAnalysisResult}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['map', 'analytics', id, 'basic'],
    enabled: !!id,
    staleTime: Infinity,
    select,
  });
};

export const usePresumePopulationPointBy = (item: AnalysisItem) => {
  const request = useGqlRequest({query: PRESUME_POPULATION_POINT_BY});

  const queryFn = ():
    | undefined
    | Promise<{
        result?: PresumePopulationPointByResult;
      }> => {
    const variant: AreaAnalyticsVars | undefined = getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    return request(variant);
  };

  const id = item?.id;

  const select = (data?: {result?: PresumePopulationPointByResult}) =>
    data?.result;

  return useQuery({
    queryFn,
    queryKey: ['map', 'analytics', id, 'presume-point'],
    enabled: !!id,
    staleTime: Infinity,
    select,
  });
};

export const useOfficeWorkerPointBy = (item: AnalysisItem) => {
  const request = useGqlRequest({query: OFFICE_WORKER_POINT_BY});

  const queryFn = ():
    | undefined
    | Promise<{
        result?: OfficeWorkerPointByResult;
      }> => {
    const variant: AreaAnalyticsVars | undefined = getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    return request({
      pointRequest: {categories: [], ...variant},
      type: 'Point',
    });
  };

  const id = item?.id;

  const select = (data?: {result?: OfficeWorkerPointByResult}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['map', 'analytics', id, 'office-worker-point'],
    enabled: !!id,
    staleTime: Infinity,
    select,
  });
};

export const useRemoveHouseholdRQData = () => {
  const client = useQueryClient();

  const remove = (id: string) => {
    client.removeQueries({
      queryKey: ['map', 'analytics', id],
    });
  };

  return remove;
};

const BASIC_RESULT = `
  house {
    type
    total
    locationInfo {
      count
      location {
        coordinates
      }
    }
  }
  upcomingAparts {
    count
    date
    location {
      coordinates
      type
    }
    name
  }
  officeWorker
  population
  average {
    house
    household
    officeWorker
    population
    sido
  }
  hallSales
`;

const AREA_ANALYZER_POINT = gql`
  query areaAnalyzerPoint(
    $lat: Float!
    $lng: Float!
    $radius: Float!
  ) {
    result: areaAnalyzerPoint(
      lat: $lat
      lng: $lng
      radius: $radius
    ) {
      ${BASIC_RESULT}
    }
  }
`;

const AREA_ANALYZER_POLYGON = gql`
  query areaAnalyzerPolygon($polygon: GeoPolygonArgs!, $m2: Float) {
    result: areaAnalyzerPolygon(polygon: $polygon, m2: $m2) {
      ${BASIC_RESULT}
    }
  }
`;

const AREA_ANALYZER_HJD = gql`
  query areaAnalyzerHjd($admCode: String!) {
    result: areaAnalyzerHjd(admCode: $admCode) {
      ${BASIC_RESULT}
    }
  }
`;

const PRESUME_POPULATION_POINT_BY = `
  query presumePopulationPointBy($lat: Float!, $lng: Float!, $radius: Float!) {
    result: presumePopulationPointBy(lat: $lat, lng: $lng, radius: $radius) {
      groupAge {
        female
        male
        sum
      }
    }
  }
`;

const OFFICE_WORKER_POINT_BY = `
  query officeWorkerReportByQuery($type: ReportType!, $pointRequest: ReportPointRequest) {
    result: officeWorkerReportByQuery(type: $type, pointRequest: $pointRequest) {
      officeWorker {
        female
        male
      }
    }
  }
`;
